import { useState, useEffect, SyntheticEvent } from "react";
import axios from "axios";
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { ConfirmDialog, DateField, StatCard, type DataTableColumn } from "@routes-plan/ui";
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString, operationalTimezone } from '../../utils/operationalDateTime';
import { ExpenseCategory } from '../../models/expense-category';
import { useResourcePermissions } from "../../hooks/useResourcePermissions";
import { useT } from "../../i18n";
import { setPageTitle } from "../../utils/pageTitle";
import { formatCurrency as companyMoney } from '../../utils/format/currency';
import {
  CheckMultiSelect,
  OfficeSelect,
  ReportEmpty,
  ReportFilters,
  ReportPage,
  ReportResults,
  ReportSelect,
  ReportStats,
  ReportTable,
  RouteMultiSelect,
  RowAction,
} from "./components/ReportLayout";

// Interfaces
interface Office { id: number; name: string; }
interface Route { id: number; name: string; }
interface ExpenseReportRow {
  id: string;
  recordId?: number;
  source: string;
  date: string;
  createdAt?: string;
  expense: string;
  description: string;
  value: number;
  office: string;
  route: string;
  isTotal?: boolean;
}
interface FilterState {
  startDate: string; endDate: string; officeId: string; selectedRouteIds: number[];
}
interface FilterStateExpense {
  startDate: string; endDate: string; officeId: string; selectedExpense: number[];
}

interface ReporteGastosProps {
  showAlert: (message: string, severity?: "error" | "info" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const HIDDEN_COLLECT_EXPENSE_NAMES = new Set([
  "CIERRE EFECTIVO RUTA",
  "BASE EFECTIVO RUTA",
]);

// Utils
const formatCurrency = (value: number): string =>
  companyMoney(value);

const normalizeExpenseName = (value: string): string => String(value || "").trim().toUpperCase();

const formatDate = (dateString: string): string => {
  if (!dateString) return "";
  const isDateTime = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(dateString);
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const parsed = isDateTime
    ? moment.tz(dateString, "YYYY-MM-DD HH:mm:ss", operationalTimezone())
    : isDateOnly
    ? moment.tz(dateString, "YYYY-MM-DD", operationalTimezone())
    : moment.tz(dateString, operationalTimezone());
  return parsed.isValid()
    ? parsed.format("DD/MM/YYYY HH:mm:ss")
    : dateString;
};

const isCurrentOperationalDate = (dateString: string, operationalDate: string): boolean => {
  if (!dateString) return false;

  const parsed = moment.tz(
    dateString,
    ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', moment.ISO_8601],
    operationalTimezone()
  );

  return parsed.isValid() && parsed.format('YYYY-MM-DD') === operationalDate;
};

// Componente
const Expenses: React.FC<ReporteGastosProps> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  setPageTitle("cashReports.expenses.pageTitle");
  const currentDate = nowOperationalDateString();
  const [datasE, setDatasE] = useState<any[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [typeexpense, setTypeExpense] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate, endDate: currentDate, officeId: "", selectedRouteIds: [],
  });

  const [filterExpense, setFilterExpense] = useState<FilterStateExpense>({
    startDate: currentDate, endDate: currentDate, officeId: "", selectedExpense: [],
  });
  const [offices, setOffices] = useState<Office[]>([]);
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const [selectAll, setSelectAll] = useState(false); // maestro para cobros
  const [selectAllExpenses, setSelectAllExpenses] = useState(false); // maestro para gastos
  const [rows, setRows] = useState<ExpenseReportRow[]>([]);
  const [queried, setQueried] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<ExpenseReportRow | null>(null);
  const { canDelete } = useResourcePermissions('/reports/expenses');

  // Oficinas
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await axios.get("/offices");
        if (response.data?.data) setOffices(response.data.data);
      } catch (error) {
        console.error("Error fetching offices:", error);
        showAlert(t("cashReports.expenses.loadOfficesError"), "error");
      }
    };
    fetchOffices();
    // Solo debe correr al montar. showAlert cambia de referencia con renders del padre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cobros por oficina + tipos de gasto
  useEffect(() => {
    const fetchRoutesAndTypes = async () => {
      if (!filter.officeId) return;

      setLoading(true);
      try {
        const response = await axios.get(`/offices/${filter.officeId}/routes`);
        if (response.data) {
          const nextRoutes = Array.isArray(response.data) ? response.data : [];
          setOfficeRoutes(nextRoutes);
          setFilter((prev) => ({
            ...prev,
            selectedRouteIds: prev.selectedRouteIds.filter((selectedId) =>
              nextRoutes.some((route: Route) => Number(route.id) === Number(selectedId))
            ),
          }));
          setSelectAll(false);
        }
      } catch (error) {
        console.error("Error fetching routes:", error);
        showAlert(t("cashReports.expenses.loadRoutesError"), "error");
      } finally {
        setLoading(false);
      }
      try {
        const { data: expenseData } = await axios.get('expense-categories');
        const expenseTypes = expenseData?.data ?? expenseData ?? [];
        setTypeExpense(Array.isArray(expenseTypes) ? expenseTypes : []);
      } catch (error) {
        showAlert(t('cashReports.expenses.loadCategoriesError'), 'error');
      }
    };
    fetchRoutesAndTypes();
    // Debe reaccionar solo al cambio real de oficina.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.officeId]);

  // Gastos por tipo seleccionado
  useEffect(() => {
    let cancelled = false;
    const fetchExpensesByType = async () => {
      if (!categoryId) {
        if (!cancelled) {
          setDatasE([]);
          setFilterExpense((prev) => ({ ...prev, selectedExpense: [] }));
          setSelectAllExpenses(false);
        }
        return;
      }

      try {
        const response = await axios.get(`/expense-categories/${categoryId}/expense-types`);
        if (!cancelled) {
          const expenses = response.data?.data ?? response.data ?? [];
          const filteredExpenses = Array.isArray(expenses)
            ? expenses.filter((expense: { name?: string }) => {
                const normalizedName = normalizeExpenseName(expense?.name || "");
                return !HIDDEN_COLLECT_EXPENSE_NAMES.has(normalizedName);
              })
            : [];
          setDatasE(filteredExpenses);
          setFilterExpense((prev) => ({
            ...prev,
            selectedExpense: prev.selectedExpense.filter((selectedId) =>
              filteredExpenses.some((expense: { id: number }) => Number(expense.id) === Number(selectedId))
            ),
          }));
          setSelectAllExpenses(false);
        }
      } catch (error) {
        console.error('Error fetching expenses by type:', error);
        showAlert(t('cashReports.expenses.loadExpensesByCategoryError'), 'error');
      }
    };
    fetchExpensesByType();
    return () => { cancelled = true; };
    // Debe reaccionar solo al cambio real de tipo de gasto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId]);

  const validateForm = (): boolean => {
    if (!filter.officeId) {
      showAlert(t("cashReports.expenses.officeRequired"), "warning");
      return false;
    }
    if (filter.selectedRouteIds.length === 0) {
      showAlert(t("cashReports.expenses.routeRequired"), "warning");
      return false;
    }
    if (filterExpense.selectedExpense.length === 0) {
      showAlert(t("cashReports.expenses.expenseRequired"), "warning");
      return false;
    }
    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert(t("cashReports.expenses.invalidRange"), "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    showLoading();
    setQueried(true);

    try {
      const payload = {
        startDate: filter.startDate,
        endDate: filter.endDate,
        officeId: filter.officeId,
        routeIds: filter.selectedRouteIds,
        expenseIds: filterExpense.selectedExpense,
      };

      const response = await axios.post("/reports/expenses", payload);
      const reportRows: ExpenseReportRow[] = Array.isArray(response.data?.data)
        ? response.data.data
          .filter((row: ExpenseReportRow) => {
            if (row.source !== "COBRO") return true;
            return !HIDDEN_COLLECT_EXPENSE_NAMES.has(normalizeExpenseName(row.expense));
          })
          .map((row: ExpenseReportRow, index: number) => ({
            ...row,
            id: row.id || `row-${index}`,
          }))
        : [];

      if (reportRows.length === 0) {
        showAlert(t("cashReports.expenses.noResults"), "info");
        setRows([]);
        return;
      }

      setRows(reportRows);
      showAlert(t("cashReports.expenses.found", { count: reportRows.length }), "success");
    } catch (error) {
      console.error("Error fetching expenses:", error);
      showAlert(t("cashReports.expenses.loadError"), "error");
    } finally {
      hideLoading();
    }
  };

  const handleDeleteExpense = async (row: ExpenseReportRow) => {
    if (!row.recordId || !['OFICINA', 'COBRO'].includes(row.source) || row.isTotal) {
      return;
    }

    if (!isCurrentOperationalDate(row.date, currentDate)) {
      showAlert(t('cashReports.expenses.onlyTodayDelete'), 'warning');
      return;
    }

    showLoading();

    try {
      await axios.delete(`/reports/expenses/${row.source}/${row.recordId}`);
      setRows((prevRows) => prevRows.filter((item) => item.id !== row.id));
      showAlert(t('cashReports.expenses.deletedOk'), 'success');
    } catch (error: any) {
      const message =
        error?.response?.data?.message || t('cashReports.expenses.deleteError');
      showAlert(message, 'error');
    } finally {
      hideLoading();
    }
  };

  const totalValue = rows.reduce((sum, row) => sum + Number(row.value ?? 0), 0);

  const canDeleteRow = (row: ExpenseReportRow) =>
    !row.isTotal && ['OFICINA', 'COBRO'].includes(row.source) && Boolean(row.recordId) && isCurrentOperationalDate(row.date, currentDate);

  const columns: DataTableColumn<ExpenseReportRow>[] = [
    { id: 'date', header: t('common.date'), value: (r) => r.createdAt || r.date, render: (r) => formatDate(r.createdAt || r.date), csv: (r) => formatDate(r.createdAt || r.date), sortable: true },
    { id: 'expense', header: t('cashReports.expenses.expense'), value: (r) => r.expense, sortable: true },
    { id: 'route', header: t('summaryReports.shared.route'), value: (r) => r.route, sortable: true },
    { id: 'value', header: t('cashReports.value'), value: (r) => Number(r.value ?? 0), render: (r) => formatCurrency(Number(r.value ?? 0)), align: 'right', sortable: true },
    { id: 'description', header: t('cashReports.description'), value: (r) => r.description, wrap: true, hideBelow: 'sm' },
  ];

  return (
    <ReportPage title={t("cashReports.expenses.title")} description={t("cashReports.expenses.description")}>
      <ReportFilters onSubmit={() => handleSubmit({ preventDefault: () => undefined } as SyntheticEvent)}>
        <DateField
          label={t("cashReports.startDate")}
          value={filter.startDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, startDate: v ?? "" }))}
          max={minYmd(filter.endDate, nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t("cashReports.endDate")}
          value={filter.endDate}
          onChange={(v) => setFilter((prev) => ({ ...prev, endDate: v ?? "" }))}
          min={filter.startDate}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={filter.officeId} onChange={(id) => setFilter((prev) => ({ ...prev, officeId: id }))} />
        <RouteMultiSelect
          routes={officeRoutes}
          value={filter.selectedRouteIds.map(String)}
          onChange={(ids) => setFilter((prev) => ({ ...prev, selectedRouteIds: ids.map(Number) }))}
          disabled={loading}
        />
        <ReportSelect
          label={t("cashReports.expenses.expenseCategory")}
          options={typeexpense.map((te) => ({ id: te.id, name: te.name }))}
          value={categoryId}
          onChange={(value) => {
            setCategoryId(value);
            setDatasE([]);
            setSelectAllExpenses(false);
          }}
          emptyOption={t("cashReports.expenses.selectExpenseCategory")}
          required
        />
        <CheckMultiSelect
          label={t("cashReports.expenses.expenses")}
          options={datasE.map((exp: { id: number; name: string }) => ({ id: exp.id, name: exp.name }))}
          value={filterExpense.selectedExpense.map(String)}
          onChange={(ids) => {
            setFilterExpense((prev) => ({ ...prev, selectedExpense: ids.map(Number) }));
            setSelectAllExpenses(ids.length > 0 && ids.length === datasE.length);
          }}
          allOption={t("cashReports.expenses.allExpenses")}
          allSelectedText={(count) => t("cashReports.expenses.allExpensesCount", { count })}
          someSelectedText={(count) => t("cashReports.expenses.expensesSelected", { count })}
        />
      </ReportFilters>

      {rows.length > 0 && (
        <ReportStats>
          <StatCard label={t("cashReports.expenses.count")} value={rows.length} />
          <StatCard label={t("cashReports.expenses.total")} value={formatCurrency(totalValue)} />
        </ReportStats>
      )}

      <ReportResults>
        <ReportTable<ExpenseReportRow>
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          exportFileName={t("cashReports.expenses.exportFileName")}
          ariaLabel={t("cashReports.expenses.title")}
          emptyState={<ReportEmpty queried={queried} />}
          rowActions={(row) =>
            canDeleteRow(row) ? (
              <RowAction
                allowed={canDelete}
                deniedTooltip={t("cashReports.expenses.noPermissionDelete")}
                label={t("common.delete")}
                color="error"
                icon={<DeleteOutlined fontSize="small" />}
                onClick={() => setPendingDelete(row)}
              />
            ) : null
          }
        />
      </ReportResults>

      <ConfirmDialog
        open={pendingDelete !== null}
        variant="danger"
        title={t("cashReports.expenses.confirmDeleteTitle")}
        message={t("cashReports.expenses.confirmDelete")}
        confirmLabel={t("common.delete")}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const row = pendingDelete;
          setPendingDelete(null);
          if (row) void handleDeleteExpense(row);
        }}
      />
    </ReportPage>
  );
};
export default Expenses;
